import React, {
  memo,
  useDeferredValue,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link, useNavigate } from "react-router-dom";
import ReactFlow, {
  Background,
  Controls,
  Handle,
  MarkerType,
  MiniMap,
  Position,
} from "reactflow";
import "reactflow/dist/style.css";
import {
  ArrowUpRight,
  Home,
  Maximize2,
  Minimize2,
  Network,
  Search,
} from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { sitemap } from "./sitemapData";
import "../styles/SiteMapModern.css";

const ROOT_FLOW_ID = "utahdata-root";
const NODE_GAP_X = 310;
const NODE_GAP_Y = 180;
const FLOW_VIEW_PADDING = 0.18;
const NODE_FOCUS_WIDTH = 214;
const NODE_FOCUS_HEIGHT = 132;

const scheduleIdleWork = (callback) => {
  if (typeof window === "undefined") {
    return { cancel: () => {} };
  }

  if ("requestIdleCallback" in window) {
    const idleId = window.requestIdleCallback(callback, { timeout: 220 });
    return {
      cancel: () => window.cancelIdleCallback(idleId),
    };
  }

  const timeoutId = window.setTimeout(callback, 90);
  return {
    cancel: () => window.clearTimeout(timeoutId),
  };
};

const sectionMeta = {
  "Utah Verticals": {
    accent: "#0f766e",
    soft: "#d7f5ee",
    glow: "rgba(15, 118, 110, 0.18)",
    edge: "#0d9488",
    description:
      "Industry pages and economic sectors organized as an interactive branch.",
  },
  "Utah At a Glance": {
    accent: "#1d4ed8",
    soft: "#dbeafe",
    glow: "rgba(37, 99, 235, 0.18)",
    edge: "#3b82f6",
    description:
      "Public-interest pages for communities, education, infrastructure, and jobs.",
  },
  "Other Pages": {
    accent: "#b45309",
    soft: "#fef3c7",
    glow: "rgba(245, 158, 11, 0.2)",
    edge: "#f59e0b",
    description:
      "Utility, support, policy, and account-related pages across UtahData.org.",
  },
};

const getSectionMeta = (label) =>
  sectionMeta[label] || {
    accent: "#0f766e",
    soft: "#ccfbf1",
    glow: "rgba(13, 148, 136, 0.18)",
    edge: "#14b8a6",
    description: "A sitemap branch within UtahData.org.",
  };

const slugify = (value) =>
  (value || "node")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

const countNodes = (nodes = []) =>
  nodes.reduce(
    (total, node) => total + 1 + countNodes(node.children || []),
    0,
  );

const filterNode = (node, query) => {
  const normalizedQuery = query.trim().toLowerCase();

  if (!normalizedQuery) {
    return node;
  }

  const matchingChildren = (node.children || [])
    .map((child) => filterNode(child, normalizedQuery))
    .filter(Boolean);

  const selfMatches =
    node.label.toLowerCase().includes(normalizedQuery) ||
    (node.path || "").toLowerCase().includes(normalizedQuery);

  if (selfMatches || matchingChildren.length) {
    return {
      ...node,
      children: matchingChildren,
    };
  }

  return null;
};

const buildVisibleTree = (rootNode, activeSection, query) => {
  const scopedRoot =
    activeSection === "all"
      ? rootNode
      : {
          ...rootNode,
          children: (rootNode.children || []).filter(
            (section) => section.label === activeSection,
          ),
        };

  return filterNode(scopedRoot, query) || {
    ...scopedRoot,
    children: [],
  };
};

const SiteNode = memo(({ data, selected }) => {
  const meta = getSectionMeta(data.sectionLabel);
  const nodeType =
    data.depth === 0
      ? "Root"
      : data.depth === 1
        ? "Section"
        : data.childCount
          ? "Branch"
          : "Page";

  return (
    <div
      className={`sitemap-flow-node${selected ? " selected" : ""}${
        data.isMatch ? " matched" : ""
      }`}
      style={{
        "--node-accent": meta.accent,
        "--node-soft": meta.soft,
        "--node-glow": meta.glow,
        "--node-edge": meta.edge,
      }}
    >
      <Handle type="target" position={Position.Top} className="sitemap-flow-handle" />
      <span className="sitemap-flow-nodeEyebrow">{nodeType}</span>
      <div className="sitemap-flow-nodeTop">
        <strong>{data.label}</strong>
        {data.path ? <ArrowUpRight size={14} strokeWidth={2.4} /> : null}
      </div>
      <p>{data.path || "Grouping node"}</p>
      <div className="sitemap-flow-nodeMeta">
        <span>
          {data.childCount} child{data.childCount !== 1 ? "ren" : ""}
        </span>
        <span>{data.sectionLabel}</span>
      </div>
      <Handle
        type="source"
        position={Position.Bottom}
        className="sitemap-flow-handle"
      />
    </div>
  );
});

const nodeTypes = {
  siteNode: SiteNode,
};

const buildFlowLayout = (
  node,
  { depth = 0, sectionLabel = node.label, nodeId = ROOT_FLOW_ID, query = "" } = {},
) => {
  const children = node.children || [];
  const meta = getSectionMeta(sectionLabel);
  let width = 0;
  const childCenters = [];
  const childLayouts = [];

  children.forEach((child, index) => {
    const childId = `${nodeId}-${slugify(child.label)}-${index}`;
    const nextSection = depth === 0 ? child.label : sectionLabel;
    const childLayout = buildFlowLayout(child, {
      depth: depth + 1,
      sectionLabel: nextSection,
      nodeId: childId,
      query,
    });

    const shiftX = width * NODE_GAP_X;

    childLayouts.push({
      childId,
      shiftX,
      layout: childLayout,
    });
    childCenters.push(childLayout.rootCenter + shiftX);
    width += childLayout.width;
  });

  const rootCenter =
    childCenters.length > 0
      ? (childCenters[0] + childCenters[childCenters.length - 1]) / 2
      : 0;

  const normalizedQuery = query.trim().toLowerCase();
  const isMatch = normalizedQuery
    ? node.label.toLowerCase().includes(normalizedQuery) ||
      (node.path || "").toLowerCase().includes(normalizedQuery)
    : false;

  const currentNode = {
    id: nodeId,
    type: "siteNode",
    position: {
      x: rootCenter,
      y: depth * NODE_GAP_Y,
    },
    data: {
      id: nodeId,
      label: node.label,
      path: node.path || "",
      depth,
      childCount: children.length,
      sectionLabel,
      isMatch,
      childLinks: children.map((childItem) => ({
        label: childItem.label,
        path: childItem.path || "",
      })),
    },
  };

  const nodes = [currentNode];
  const edges = [];

  childLayouts.forEach(({ childId, shiftX, layout }) => {
    nodes.push(
      ...layout.nodes.map((flowNode) => ({
        ...flowNode,
        position: {
          x: flowNode.position.x + shiftX,
          y: flowNode.position.y,
        },
      })),
    );

    edges.push(...layout.edges);

    const edgeAccent =
      depth === 0
        ? getSectionMeta(layout.sectionLabel).accent
        : meta.accent;

    edges.push({
      id: `${nodeId}-${childId}`,
      source: nodeId,
      target: childId,
      type: "smoothstep",
      markerEnd: {
        type: MarkerType.ArrowClosed,
        color: edgeAccent,
      },
      animated: false,
      style: {
        stroke: edgeAccent,
        strokeWidth: depth === 0 ? 2.2 : 1.5,
        opacity: 0.48,
      },
    });
  });

  return {
    width: Math.max(1, width),
    rootCenter,
    nodes,
    edges,
    sectionLabel,
  };
};

function SiteMapModern() {
  const canvasShellRef = useRef(null);
  const searchInputRef = useRef(null);
  const navigate = useNavigate();
  const rootNode = sitemap[0];
  const availableSections = rootNode.children || [];
  const [searchTerm, setSearchTerm] = useState("");
  const [activeSection, setActiveSection] = useState("all");
  const [activeNodeId, setActiveNodeId] = useState(ROOT_FLOW_ID);
  const [reactFlowInstance, setReactFlowInstance] = useState(null);
  const [isFlowReady, setIsFlowReady] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showMiniMap, setShowMiniMap] = useState(false);
  const deferredSearchTerm = useDeferredValue(searchTerm);

  useEffect(() => {
    const flowReadyTask = scheduleIdleWork(() => {
      setIsFlowReady(true);
    });

    return () => {
      flowReadyTask.cancel();
    };
  }, []);

  useEffect(() => {
    if (!isFlowReady) {
      return undefined;
    }

    const miniMapTask = scheduleIdleWork(() => {
      setShowMiniMap(true);
    });

    return () => {
      miniMapTask.cancel();
    };
  }, [isFlowReady]);

  useEffect(() => {
    if (typeof document === "undefined") {
      return undefined;
    }

    const handleFullscreenChange = () => {
      setIsFullscreen(document.fullscreenElement === canvasShellRef.current);
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);

    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
    };
  }, []);

  const visibleTree = useMemo(
    () =>
      buildVisibleTree(
        rootNode,
        activeSection,
        deferredSearchTerm,
      ),
    [activeSection, deferredSearchTerm, rootNode],
  );

  const flowLayout = useMemo(
    () =>
      buildFlowLayout(visibleTree, {
        nodeId: ROOT_FLOW_ID,
        query: deferredSearchTerm,
      }),
    [deferredSearchTerm, visibleTree],
  );

  const visibleNodeIds = useMemo(
    () => new Set(flowLayout.nodes.map((node) => node.id)),
    [flowLayout.nodes],
  );
  const matchingNodes = useMemo(
    () => flowLayout.nodes.filter((node) => node.data.isMatch),
    [flowLayout.nodes],
  );
  const firstMatchingNode = matchingNodes[0] || null;
  const selectedNode = useMemo(
    () =>
      flowLayout.nodes.find((node) => node.id === activeNodeId)?.data ||
      flowLayout.nodes[0]?.data ||
      null,
    [activeNodeId, flowLayout.nodes],
  );
  const activeMeta = getSectionMeta(selectedNode?.sectionLabel || rootNode.label);

  const visibleDestinations = useMemo(
    () => countNodes(visibleTree.children || []),
    [visibleTree],
  );

  useEffect(() => {
    if (!activeNodeId || !visibleNodeIds.has(activeNodeId)) {
      setActiveNodeId(flowLayout.nodes[0]?.id || ROOT_FLOW_ID);
    }
  }, [activeNodeId, flowLayout.nodes, visibleNodeIds]);

  useEffect(() => {
    if (!deferredSearchTerm || !firstMatchingNode) {
      return;
    }

    if (activeNodeId !== firstMatchingNode.id) {
      setActiveNodeId(firstMatchingNode.id);
    }
  }, [activeNodeId, deferredSearchTerm, firstMatchingNode]);

  useEffect(() => {
    if (!reactFlowInstance || !isFlowReady || !flowLayout.nodes.length) {
      return undefined;
    }

    const frame = window.requestAnimationFrame(() => {
      reactFlowInstance.fitView({
        padding: FLOW_VIEW_PADDING,
        duration: 260,
        maxZoom: 1.15,
      });
    });

    return () => window.cancelAnimationFrame(frame);
  }, [
    reactFlowInstance,
    isFlowReady,
    isFullscreen,
    activeSection,
    deferredSearchTerm,
    flowLayout.nodes.length,
  ]);

  useEffect(() => {
    if (
      !reactFlowInstance ||
      !isFlowReady ||
      !deferredSearchTerm ||
      !firstMatchingNode
    ) {
      return undefined;
    }

    const frame = window.requestAnimationFrame(() => {
      reactFlowInstance.setCenter(
        firstMatchingNode.position.x + NODE_FOCUS_WIDTH / 2,
        firstMatchingNode.position.y + NODE_FOCUS_HEIGHT / 2,
        {
          zoom: 1.02,
          duration: 280,
        },
      );
    });

    return () => window.cancelAnimationFrame(frame);
  }, [
    reactFlowInstance,
    isFlowReady,
    deferredSearchTerm,
    firstMatchingNode,
    isFullscreen,
  ]);

  const handleToggleFullscreen = async () => {
    const canvasElement = canvasShellRef.current;

    if (!canvasElement || typeof document === "undefined") {
      return;
    }

    try {
      if (document.fullscreenElement === canvasElement) {
        await document.exitFullscreen();
        return;
      }

      await canvasElement.requestFullscreen();
    } catch (error) {
      console.error("Unable to toggle sitemap fullscreen mode.", error);
    }
  };

  return (
    <div className="sitemap-flow-page">
      <Header />

      <main className="sitemap-flow-main">
        <section className="sitemap-flow-shell">
          <div className="sitemap-flow-toolbar">
            <label
              className="sitemap-flow-search"
              onClick={() => searchInputRef.current?.focus()}
            >
              <span className="sitemap-flow-searchIcon" aria-hidden="true">
                <Search size={18} />
              </span>
              <input
                ref={searchInputRef}
                type="search"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder="Search sitemap labels or route paths"
                aria-label="Search sitemap"
              />
            </label>

            <div className="sitemap-flow-chipRow">
              <button
                type="button"
                className={activeSection === "all" ? "active" : ""}
                onClick={() => setActiveSection("all")}
              >
                All sections
              </button>
              {availableSections.map((section) => (
                <button
                  key={section.label}
                  type="button"
                  className={activeSection === section.label ? "active" : ""}
                  onClick={() => setActiveSection(section.label)}
                >
                  {section.label}
                </button>
              ))}
            </div>
          </div>

          <div className="sitemap-flow-workspace">
            <div
              ref={canvasShellRef}
              className={`sitemap-flow-canvasShell${
                isFullscreen ? " is-fullscreen" : ""
              }`}
            >
              <div className="sitemap-flow-canvasHeader">
                <div>
                  <span className="sitemap-flow-rootLabel">Sitemap</span>
                  <h2>{visibleDestinations} destinations</h2>
                </div>
                <div className="sitemap-flow-canvasMeta">
                  <button
                    type="button"
                    className="sitemap-flow-fullscreenButton"
                    onClick={handleToggleFullscreen}
                    aria-pressed={isFullscreen}
                    aria-label={
                      isFullscreen
                        ? "Exit fullscreen sitemap view"
                        : "Open sitemap in fullscreen"
                    }
                  >
                    {isFullscreen ? <Minimize2 size={15} /> : <Maximize2 size={15} />}
                    <span>{isFullscreen ? "Exit full screen" : "Full screen"}</span>
                  </button>
                  <span>
                    {deferredSearchTerm
                      ? `Search: "${deferredSearchTerm}"`
                      : "Full tree"}
                  </span>
                  <span>
                    {activeSection === "all" ? "All sections" : activeSection}
                  </span>
                </div>
              </div>

              <div className="sitemap-flow-stage">
                <div className="sitemap-flow-stageBadge">
                  <Network size={16} />
                  <span>
                    {isFlowReady
                      ? "Drag to move, scroll to zoom, double-click node to open"
                      : "Preparing sitemap view"}
                  </span>
                </div>

                {isFlowReady ? (
                  <ReactFlow
                    nodes={flowLayout.nodes}
                    edges={flowLayout.edges}
                    nodeTypes={nodeTypes}
                    defaultViewport={{ x: 0, y: 0, zoom: 0.72 }}
                    proOptions={{ hideAttribution: true }}
                    nodesConnectable={false}
                    nodesDraggable={false}
                    onlyRenderVisibleElements
                    onInit={setReactFlowInstance}
                    onNodeClick={(_, node) => setActiveNodeId(node.id)}
                    onNodeDoubleClick={(_, node) => {
                      if (node.data.path) {
                        navigate(node.data.path);
                      }
                    }}
                  >
                    {showMiniMap ? (
                      <MiniMap
                        pannable
                        zoomable
                        nodeColor={(node) => getSectionMeta(node.data.sectionLabel).accent}
                        maskColor="rgba(15, 23, 42, 0.08)"
                      />
                    ) : null}
                    <Controls showInteractive={false} />
                    <Background
                      color="rgba(148, 163, 184, 0.28)"
                      gap={22}
                      size={1.2}
                    />
                  </ReactFlow>
                ) : (
                  <div className="sitemap-flow-stageLoading" aria-live="polite">
                    <div className="sitemap-flow-stageLoadingPulse" />
                    <strong>Loading sitemap map</strong>
                    <p>
                      The page shell loads first, then the interactive graph is
                      mounted once the browser is ready.
                    </p>
                  </div>
                )}
              </div>
            </div>

            <aside
              className="sitemap-flow-sidebar"
              style={{
                "--panel-accent": activeMeta.accent,
                "--panel-soft": activeMeta.soft,
                "--panel-glow": activeMeta.glow,
                "--panel-edge": activeMeta.edge,
              }}
            >
              {selectedNode ? (
                <>
                  <div className="sitemap-flow-sidebarHeader">
                    <span className="sitemap-flow-rootLabel">Selected node</span>
                    <h2>{selectedNode.label}</h2>
                    <p className="sitemap-flow-sidebarSubtext">
                      {selectedNode.depth === 0
                        ? "This is the sitemap root."
                        : `Part of the ${selectedNode.sectionLabel} branch.`}
                    </p>
                  </div>

                  <div className="sitemap-flow-detailGrid">
                    <div className="sitemap-flow-detailCard">
                      <span>Section</span>
                      <strong>{selectedNode.sectionLabel}</strong>
                    </div>
                    <div className="sitemap-flow-detailCard">
                      <span>Children</span>
                      <strong>{selectedNode.childCount}</strong>
                    </div>
                    <div className="sitemap-flow-detailCard">
                      <span>Depth</span>
                      <strong>{selectedNode.depth}</strong>
                    </div>
                  </div>

                  <div className="sitemap-flow-routeCard">
                    <span>Route path</span>
                    <strong className="sitemap-flow-routeValue">
                      {selectedNode.path || "No direct route"}
                    </strong>
                    {selectedNode.path ? (
                      <button
                        type="button"
                        onClick={() => navigate(selectedNode.path)}
                      >
                        Open page
                        <ArrowUpRight size={16} />
                      </button>
                    ) : null}
                  </div>

                  <div className="sitemap-flow-childList">
                    <div className="sitemap-flow-childListHeader">
                      <Home size={16} />
                      <span>Connected child nodes</span>
                    </div>
                    {selectedNode.childLinks.length ? (
                      <div className="sitemap-flow-childLinks">
                        {selectedNode.childLinks.map((child) =>
                          child.path ? (
                            <Link
                              key={`${child.label}-${child.path}`}
                              to={child.path}
                              className="sitemap-flow-childLink"
                            >
                              {child.label}
                              <ArrowUpRight size={14} />
                            </Link>
                          ) : (
                            <div
                              key={`${child.label}-group`}
                              className="sitemap-flow-childLink passive"
                            >
                              {child.label}
                            </div>
                          ),
                        )}
                      </div>
                    ) : (
                      <p className="sitemap-flow-emptyNote">
                        This node does not have child pages.
                      </p>
                    )}
                  </div>
                </>
              ) : null}
            </aside>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}

export default SiteMapModern;
